(() => {
  const STORAGE_KEY = 'ombre_attribution_v1';
  const PARAM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'gclid', 'fbclid'];
  const ATTR_PREFIX = '_ombre_';
  const CART_SYNC_COOLDOWN_MS = 4500;
  const CART_REQUEST_TIMEOUT_MS = 1500;
  let lastCartSync = 0;
  let privacyInitialized = false;
  let cartRequest = Promise.resolve();

  const routeRoot = () => window.Shopify?.routes?.root || '/';

  const safeJsonParse = (value) => {
    try {
      return value ? JSON.parse(value) : null;
    } catch (error) {
      return null;
    }
  };

  const attributionAllowed = () => {
    if (!privacyInitialized) return false;
    try {
      const privacy = window.Shopify?.customerPrivacy;
      return privacy?.analyticsProcessingAllowed() === true && privacy?.marketingAllowed() === true;
    } catch (error) {
      return false;
    }
  };

  const storedAttribution = () => {
    try {
      return safeJsonParse(window.localStorage.getItem(STORAGE_KEY)) || {};
    } catch (error) {
      return {};
    }
  };

  const readAttribution = () => attributionAllowed() ? storedAttribution() : {};

  const writeAttribution = (payload) => {
    if (!attributionAllowed()) return;
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    } catch (error) {
      // Storage can fail in private contexts; tracking should never block buying.
    }
  };

  function captureAttribution() {
    if (!attributionAllowed()) return {};
    const params = new URLSearchParams(window.location.search);
    const captured = {};

    PARAM_KEYS.forEach((key) => {
      const value = params.get(key);
      if (value) captured[key] = value.slice(0, 180);
    });

    const previous = readAttribution();
    const hasCampaignData = Object.keys(captured).length > 0;
    const next = {
      ...previous,
      ...captured,
      landing_page: previous.landing_page || window.location.href,
      referrer: previous.referrer || document.referrer || '',
      last_seen_page: window.location.href,
      updated_at: new Date().toISOString()
    };

    if (hasCampaignData || !previous.landing_page) writeAttribution(next);
    return next;
  }

  const cartAttributesFromAttribution = (attribution) => {
    const attributes = {};
    PARAM_KEYS.forEach((key) => {
      if (attribution[key]) attributes[`${ATTR_PREFIX}${key}`] = attribution[key];
    });
    if (attribution.landing_page) attributes[`${ATTR_PREFIX}landing_page`] = attribution.landing_page;
    if (attribution.referrer) attributes[`${ATTR_PREFIX}referrer`] = attribution.referrer;
    return attributes;
  };

  function sendCartAttributes(attributes, clearing = false) {
    // Serialize cleanup after pending writes so revoked identifiers cannot win a race.
    cartRequest = cartRequest.catch(() => {}).then(() => {
      if (!clearing && !attributionAllowed()) return;
      return new Promise(resolve => {
        const controller = typeof AbortController === 'function' ? new AbortController() : null;
        const timeout = setTimeout(() => {
          controller?.abort();
          clearTimeout(timeout);
          resolve();
        }, CART_REQUEST_TIMEOUT_MS);
        Promise.resolve().then(() => fetch(`${routeRoot()}cart/update.js`, {
          method: 'POST',
          headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
          body: JSON.stringify({ attributes }),
          ...(controller ? { signal: controller.signal } : {})
        })).catch(() => {}).finally(() => {
          clearTimeout(timeout);
          resolve();
        });
      });
    });
    return cartRequest;
  }

  function syncCartAttribution(attribution = readAttribution()) {
    if (!attributionAllowed()) return Promise.resolve();
    const attributes = cartAttributesFromAttribution(attribution);
    if (!Object.keys(attributes).length) return Promise.resolve();
    if (Date.now() - lastCartSync < CART_SYNC_COOLDOWN_MS) return Promise.resolve();
    lastCartSync = Date.now();

    return sendCartAttributes(attributes);
  }

  function reconcilePrivacy() {
    const privacy = window.Shopify?.customerPrivacy;
    if (typeof privacy?.analyticsProcessingAllowed !== 'function' || typeof privacy?.marketingAllowed !== 'function') return;
    privacyInitialized = true;
    const allowed = attributionAllowed();
    if (allowed) {
      return syncCartAttribution(captureAttribution());
    }

    try {
      window.localStorage.removeItem(STORAGE_KEY);
    } catch (error) {
      // Buying must still work when browser storage is unavailable.
    }
    // Cart identifiers can outlive browser storage or a failed cleanup request.
    lastCartSync = 0;
    const attributes = {};
    [...PARAM_KEYS, 'landing_page', 'referrer'].forEach(key => { attributes[`${ATTR_PREFIX}${key}`] = ''; });
    return sendCartAttributes(attributes, true);
  }

  const normalizePrice = (value) => {
    const numeric = Number(value || 0);
    if (!Number.isFinite(numeric)) return 0;
    return numeric > 999 ? numeric / 100 : numeric;
  };

  const readProductAnalyticsJson = (source) => {
    const root = source?.closest?.('[data-ombre-native-product-detail]') || document;
    const node = root.querySelector?.('[data-ombre-product-analytics]');
    return safeJsonParse(node?.textContent) || {};
  };

  const nearestProductSource = (source) => {
    if (!source?.closest) return null;
    return source.closest('[data-product-card], [data-ombre-native-product-detail], form[action*="/cart/add"]');
  };

  const buildItemFromElement = (source, overrides = {}) => {
    const productSource = nearestProductSource(source);
    const jsonPayload = readProductAnalyticsJson(productSource || source);
    const dataset = productSource?.dataset || {};
    const variantId = overrides.variantId || source?.dataset?.variantId || dataset.selectedVariantId || dataset.variantId || jsonPayload.item_id || dataset.productId;
    const price = normalizePrice(overrides.price || dataset.productPrice || dataset.price || jsonPayload.price);
    const quantity = Math.max(1, Number(overrides.quantity || dataset.quantity || 1));

    return {
      item_id: String(overrides.item_id || jsonPayload.item_id || variantId || '').trim(),
      item_name: overrides.item_name || jsonPayload.item_name || dataset.productTitle || dataset.title || document.title,
      item_brand: overrides.item_brand || jsonPayload.item_brand || dataset.productBrand || 'Ombré Noir',
      item_category: overrides.item_category || jsonPayload.item_category || dataset.productCategory || 'Watches and bracelets',
      item_variant: overrides.item_variant || jsonPayload.item_variant || dataset.variantTitle || '',
      price,
      quantity
    };
  };

  const currencyFor = (source) => {
    const productSource = nearestProductSource(source);
    const jsonPayload = readProductAnalyticsJson(productSource || source);
    return jsonPayload.currency || productSource?.dataset?.currency || window.Shopify?.currency?.active || 'USD';
  };

  // Google & YouTube owns ecommerce events, including successful cart updates.
  // Keep these theme hooks for attribution only; never broadcast duplicate hits.
  function trackAddToCart() {
    return syncCartAttribution();
  }

  function trackBeginCheckout() {
    return syncCartAttribution();
  }

  window.OmbreAnalytics = {
    buildItemFromElement,
    captureAttribution,
    readAttribution,
    syncCartAttribution,
    trackAddToCart,
    trackBeginCheckout
  };

  document.addEventListener('visitorConsentCollected', reconcilePrivacy);
  if (window.Shopify?.customerPrivacy) {
    reconcilePrivacy();
  } else if (typeof window.Shopify?.loadFeatures === 'function') {
    window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], error => {
      if (!error) reconcilePrivacy();
    });
  }

  document.addEventListener('click', (event) => {
    const addButton = event.target.closest('[data-add], [data-ombre-add-button], [data-ombre-sticky-button]');
    if (addButton && !addButton.disabled) trackAddToCart(addButton);

    const buyButton = event.target.closest('[data-buy]');
    if (buyButton && !buyButton.disabled) trackBeginCheckout(buyButton);

    const checkoutButton = event.target.closest('[name="checkout"], #CartDrawer-Checkout, .cart__checkout-button');
    if (checkoutButton && !checkoutButton.disabled) {
      syncCartAttribution();
    }
  }, true);

  document.addEventListener('submit', (event) => {
    const form = event.target.closest('form[action*="/cart/add"]');
    if (form) trackAddToCart(form);
  }, true);
})();
